/**
 * Toolbar for the Add/Edit Warehouse Delivery modal — destination, row filters,
 * the availability window controls and the container-fill readout.
 *
 * Extracted from WarehouseDeliveryModal (CR-7342) purely to keep that component
 * under the 500-line limit; the markup is unchanged apart from the new pick-up
 * date control (and CR-8191's committed-by date). Presentational only — every
 * piece of state stays owned by the modal and arrives as a prop.
 *
 * @link WarehouseDeliveryModal for the consumer.
 */
import { useState, useEffect } from 'react';
import { Label } from '../../ui/label';
import { Progress } from '../../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import LocalColumnManager from './LocalColumnManager';
import type { LocalColumn } from './DirectDeliveryColumns';
import type { WizardWarehouse } from './WarehouseDeliveryModal';
import { useFilterVisibility } from '../../../hooks/useFilterVisibility';
import PartSearchInput from '../shared/PartSearchInput';
import PartPrefixSelect from '../shared/PartPrefixSelect';
import type { PartPrefixFilter } from '../shared/partPrefixFilter';
import { COMMITTED_BY_MAX_DATE, todayCommittedBy } from '../shared/committedByDate';

interface WarehouseDeliveryToolbarProps {
  warehouses: WizardWarehouse[];
  selectedWarehouse: string;
  setSelectedWarehouse: (id: string) => void;
  columns: LocalColumn[];
  onToggleColumn: (id: string) => void;
  onMoveColumnUp: (id: string) => void;
  /** CR-7717: client-side part number / description search over the grid. */
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  partFilter: PartPrefixFilter;
  setPartFilter: (filter: PartPrefixFilter) => void;
  commodityFilter: string;
  setCommodityFilter: (value: string) => void;
  commodityOptions: string[];
  openPoDays: number;
  setOpenPoDays: (days: number) => void;
  pickupDate?: string;
  /** Omitted when the host cannot accept a date change; the control hides. */
  setPickupDate?: (date: string) => void;
  /** CR-8191: SAL "committed by" cut-off (YYYY-MM-DD); never blank. */
  committedByDate: string;
  setCommittedByDate: (date: string) => void;
  totalFillPercent: number;
  existingFillPercent: number;
  modalFillPercent: number;
  isEdit: boolean;
}

const CONTROL_CLS =
  'h-8 text-sm border border-border rounded-lg px-2 bg-background ' +
  'focus:outline-none focus:ring-2 focus:ring-primary';

export default function WarehouseDeliveryToolbar({
  warehouses, selectedWarehouse, setSelectedWarehouse,
  columns, onToggleColumn, onMoveColumnUp,
  searchTerm, setSearchTerm, partFilter, setPartFilter,
  commodityFilter, setCommodityFilter, commodityOptions,
  openPoDays, setOpenPoDays, pickupDate, setPickupDate, committedByDate, setCommittedByDate,
  totalFillPercent, existingFillPercent, modalFillPercent, isEdit,
}: WarehouseDeliveryToolbarProps) {
  // CR-4802: Commodity follows the tenant's declared filter shape.
  const fv = useFilterVisibility();
  // CR-8191: Chrome reports an empty value as soon as one part of the date is
  // deleted, so the field may sit blank while it is retyped; only a complete
  // date goes to the wizard, and leaving it empty puts today back.
  const [committedDraft, setCommittedDraft] = useState(committedByDate);
  useEffect(() => { setCommittedDraft(committedByDate); }, [committedByDate]);
  const restoreCommittedDate = () => {
    if (committedDraft) return;
    const today = todayCommittedBy();
    setCommittedDraft(today);
    setCommittedByDate(today);
  };
  return (
    <div className="flex items-end gap-4 px-6 py-3 border-b bg-muted/30">
      <div className="space-y-1">
        <Label className="text-xs">
          Warehouse <span className="text-red-500">*</span>
        </Label>
        <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
          <SelectTrigger className={`w-48 ${!selectedWarehouse ? 'border-red-300' : ''}`}>
            <SelectValue placeholder={
              warehouses.length === 0 ? 'Loading…' : 'Select warehouse'
            } />
          </SelectTrigger>
          <SelectContent>
            {warehouses.map(w => (
              <SelectItem key={w.id} value={String(w.id)}>{w.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <LocalColumnManager columns={columns} onToggle={onToggleColumn} onMoveUp={onMoveColumnUp} />
      <div className="space-y-1">
        <Label className="text-xs">Search</Label>
        <PartSearchInput value={searchTerm} onChange={setSearchTerm} className="w-56 h-8" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Parts</Label>
        <PartPrefixSelect value={partFilter} onChange={setPartFilter} className={`w-52 ${CONTROL_CLS}`} />
      </div>
      {fv.commodity && (
      <div className="space-y-1">
        <Label className="text-xs">Commodity Type</Label>
        <select
          value={commodityFilter}
          onChange={(e) => setCommodityFilter(e.target.value)}
          className={`w-44 ${CONTROL_CLS}`}
          aria-label="Commodity type filter"
        >
          <option value="all">All commodity types</option>
          {commodityOptions.map(ct => (
            <option key={ct} value={ct}>{ct}</option>
          ))}
        </select>
      </div>
      )}
      {/* CR-6293: open-PO window. Available To Call Off counts PO due within
          max(today + this, pickup date). Defaults to the stock-planning value. */}
      <div className="space-y-1">
        <Label className="text-xs">Open PO Days</Label>
        <input
          type="number"
          min={0}
          value={openPoDays}
          onChange={(e) => setOpenPoDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
          className={`w-24 ${CONTROL_CLS}`}
          aria-label="Open PO days window"
          title="PO due within this many days counts as available. A later pickup date extends the window."
        />
      </div>
      {/* CR-7342: the pick-up date drives the same availability window as Open
          PO Days above, but used to live only on the wizard screen behind this
          modal — so checking a different date meant discarding the basket and
          starting again. Changing it here re-fetches availability in place,
          because pickupDate is already a dependency of the stock-planning
          fetch. */}
      {setPickupDate && (
        <div className="space-y-1">
          <Label className="text-xs">Pick-up Date</Label>
          <input
            type="date"
            value={pickupDate || ''}
            onChange={(e) => setPickupDate(e.target.value)}
            className={CONTROL_CLS}
            aria-label="Pick-up date"
            title="A later pick-up date widens the open-PO window, so more quantity becomes available to call off."
          />
        </div>
      )}
      {/* CR-8191: SAL commitments dated on or before this count as committed
          (Committed By Date column, Current Shortage, Stock Health SH Plus).
          Was settable on Stock Planning only, so a forgotten date meant
          starting the call-off again. Changing it re-fetches in place. */}
      <div className="space-y-1">
        <Label className="text-xs">Committed By Date</Label>
        <input
          type="date"
          max={COMMITTED_BY_MAX_DATE}
          value={committedDraft}
          onChange={(e) => {
            setCommittedDraft(e.target.value);
            if (e.target.value) setCommittedByDate(e.target.value);
          }}
          onBlur={restoreCommittedDate}
          className={CONTROL_CLS}
          aria-label="Committed by date"
          title="SAL commitments dated on or before this count as committed. Defaults to today."
        />
      </div>
      <div className="flex-1" />
      <div className="text-right space-y-1">
        <Label className="text-xs">Container Fill</Label>
        <div className="flex items-center gap-2">
          <Progress value={Math.min(totalFillPercent, 100)} className="w-32 h-2" />
          <span className={`text-sm font-bold ${totalFillPercent > 90 ? 'text-red-600' : totalFillPercent > 50 ? 'text-orange-600' : 'text-green-600'}`}>
            {totalFillPercent}%
          </span>
        </div>
        {!isEdit && existingFillPercent > 0 && (
          <p className="text-xs text-muted-foreground">
            Existing: {existingFillPercent}% + This: {modalFillPercent}%
          </p>
        )}
      </div>
    </div>
  );
}
